/**
 * Verification System Events API — advisor-only diagnostics.
 *
 * Each row records an Anthropic API failure during AtWork screenshot
 * verification (deprecated model, network error, malformed response,
 * etc). The UI surfaces a banner on the file detail page when a
 * recent event is linked to that file, so the advisor can see the
 * underlying cause when uploads are persistently rejecting with 503.
 *
 * RLS restricts SELECT to the advisor email — calling these helpers
 * as the assistant returns an empty list.
 */

import supabase from './supabaseClient';

const RECENT_WINDOW_HOURS = 48;

function mapRow(row) {
  return {
    id: row.id,
    fileId: row.file_id || null,
    caseIndex: row.case_index == null ? null : row.case_index,
    attemptedAt: row.attempted_at,
    errorStatus: row.error_status == null ? null : row.error_status,
    errorMessage: row.error_message || '',
    modelUsed: row.model_used || '',
  };
}

/**
 * Most recent verification failure linked to a single file in the
 * last 48 hours. Returns null if none.
 */
export async function getLatestEventForFile(fileRowId) {
  if (!fileRowId) return null;
  const since = new Date(Date.now() - RECENT_WINDOW_HOURS * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('verification_system_events')
    .select('*')
    .eq('file_id', fileRowId)
    .gte('attempted_at', since)
    .order('attempted_at', { ascending: false })
    .limit(1);

  if (error) {
    // RLS rejects non-advisors with no rows / error — be tolerant.
    return null;
  }
  if (!data || data.length === 0) return null;
  return mapRow(data[0]);
}

/**
 * Last N verification system events across all files. Used by the
 * aggregate diagnostic modal.
 */
export async function getRecentEvents(limit = 20) {
  const { data, error } = await supabase
    .from('verification_system_events')
    .select('*')
    .order('attempted_at', { ascending: false })
    .limit(limit);

  if (error) {
    return [];
  }
  return (data || []).map(mapRow);
}
